// OWNER: FRONTEND-DIALER (drawer). Pure presentation rules for the in-call Business drawer (LEADGEN-SPEC §11,
// signals-scoring §7). No DOM, no fetching: unit-tested in tests/fd-drawer-*.test.ts.
//
// Real-data rules this file keeps:
// - Nothing is estimated. A value the server didn't send is "not measured", never a guess.
// - Live Places data (LiveCheck) is only read here; nothing in this file stores it.
// - "Open now" from the snapshot is computed only when every range for the day parses; otherwise it says so.
import type {
  DrawerData, DrawerHook, HookLiveStatus, HookUsage, LiveCheck, LiveCheckPolicy, MapRankEntry, OfferScore, ReviewMention, WeekHours,
} from '../../../../src/shared/api-leadgen';
import type { CallSummary, DispositionCode } from '../../../../src/shared/api';
import { OPENER_NEVER, OPENER_NEVER_WORDS } from '../../../../src/shared/opener-lint';
import { zonedParts } from '../time';

export type LivePart = 'site' | 'places';

// ------------------------------------------------------------------ hours (Apify openingHours text)

/**
 * One day of Apify `openingHours[].hours`, as the actor's dataset schema documents it (build 0.14.759, pulled
 * 2026-09-25): "Closed", "7 AM to 9 PM", "11 AM to 9:30 PM", "11 AM to 12 AM" (closes at midnight),
 * "12 to 9:30 AM, 11 AM to 10:30 PM" (two ranges; a start without AM/PM takes the end's).
 * Minutes from local midnight; an end past midnight is > 1440. null = text we can't read (never guessed).
 */
export type DayHours = { kind: 'closed' } | { kind: 'open24' } | { kind: 'ranges'; ranges: Array<[number, number]> };

const SPACES = /[    ]/g;
const CLOCK = /^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i;

function clockToMin(h: number, m: number, mer: string): number | null {
  if (h < 1 || h > 12 || m < 0 || m > 59) return null;
  const up = mer.toUpperCase();
  const hour = up === 'AM' ? (h === 12 ? 0 : h) : (h === 12 ? 12 : h + 12);
  return hour * 60 + m;
}

export function parseDayHours(text: string | null | undefined): DayHours | null {
  if (typeof text !== 'string') return null;
  const t = text.replace(SPACES, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  if (/^closed$/i.test(t)) return { kind: 'closed' };
  if (/^open 24 hours$/i.test(t)) return { kind: 'open24' };
  const ranges: Array<[number, number]> = [];
  for (const part of t.split(',')) {
    const pieces = part.trim().split(/\s+to\s+|\s*[–-]\s*/i);
    if (pieces.length !== 2) return null;
    const a = CLOCK.exec(pieces[0].trim());
    const b = CLOCK.exec(pieces[1].trim());
    if (!a || !b || !b[3]) return null;
    const endMer = b[3];
    const start = clockToMin(Number(a[1]), Number(a[2] ?? 0), a[3] ?? endMer);
    let end = clockToMin(Number(b[1]), Number(b[2] ?? 0), endMer);
    if (start === null || end === null) return null;
    if (end <= start) end += 1440; // "11 AM to 12 AM" closes at midnight; "6 PM to 2 AM" runs past it
    ranges.push([start, end]);
  }
  return ranges.length ? { kind: 'ranges', ranges } : null;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function hoursFor(week: WeekHours, weekday: number): DayHours | null | undefined {
  const name = DAY_NAMES[weekday].toLowerCase();
  const row = week.find((d) => typeof d?.day === 'string' && d.day.trim().toLowerCase() === name);
  return row ? parseDayHours(row.hours) : undefined;
}

export interface OpenState { open: boolean; closesAtMin: number | null; open24: boolean }

/** Open or closed at `now` in `tz` by the snapshot hours; null when the hours for today (or yesterday's late close) can't be read. */
export function openFromHours(week: WeekHours | null | undefined, now: Date, tz: string): OpenState | null {
  if (!week || !week.length) return null;
  let p;
  try { p = zonedParts(now, tz); } catch { return null; }
  const minute = p.hour * 60 + p.minute;
  const today = hoursFor(week, p.weekday);
  const yesterday = hoursFor(week, (p.weekday + 6) % 7);
  if (!today) return null; // missing day or unreadable text: say nothing
  if (today.kind === 'open24') return { open: true, closesAtMin: null, open24: true };
  // A range from yesterday that runs past midnight
  if (yesterday && yesterday.kind === 'ranges') {
    for (const [, end] of yesterday.ranges) if (end > 1440 && minute < end - 1440) return { open: true, closesAtMin: end - 1440, open24: false };
  }
  if (today.kind === 'closed') return { open: false, closesAtMin: null, open24: false };
  for (const [start, end] of today.ranges) if (minute >= start && minute < end) return { open: true, closesAtMin: end % 1440, open24: false };
  return { open: false, closesAtMin: null, open24: false };
}

/** 'noon', 'midnight', '5 PM', '9:30 PM'. */
export function fmtMinutes(min: number): string {
  const m = ((min % 1440) + 1440) % 1440;
  if (m === 0) return 'midnight';
  if (m === 720) return 'noon';
  const h24 = Math.floor(m / 60);
  const mm = m % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}${mm ? `:${String(mm).padStart(2, '0')}` : ''} ${h24 < 12 ? 'AM' : 'PM'}`;
}

const SHORT_DAY: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};
const WEEK_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/** Google's live weekdayDescriptions ("Monday: 8:00 AM – 5:00 PM") into the same {day, hours} rows. */
export function weekFromDescriptions(lines: string[] | null | undefined): WeekHours | null {
  if (!lines || !lines.length) return null;
  const out: WeekHours = [];
  for (const line of lines) {
    const i = line.indexOf(':');
    if (i <= 0) return null;
    out.push({ day: line.slice(0, i).trim(), hours: line.slice(i + 1).trim() });
  }
  return out;
}

/** One line for the week, consecutive days with the same text grouped: "Mon–Fri 8 AM to 5 PM · Sat, Sun Closed". */
export function weekLine(week: WeekHours | null | undefined): string | null {
  if (!week || !week.length) return null;
  const rows = week
    .map((d) => ({ key: (d.day ?? '').trim().toLowerCase(), text: (d.hours ?? '').replace(SPACES, ' ').replace(/\s+/g, ' ').trim() }))
    .filter((d) => SHORT_DAY[d.key] && d.text)
    .sort((a, b) => WEEK_ORDER.indexOf(a.key) - WEEK_ORDER.indexOf(b.key));
  if (!rows.length) return null;
  const groups: Array<{ days: string[]; idx: number[]; text: string }> = [];
  for (const r of rows) {
    const idx = WEEK_ORDER.indexOf(r.key);
    const g = groups.find((x) => x.text === r.text);
    if (g) { g.days.push(r.key); g.idx.push(idx); } else groups.push({ days: [r.key], idx: [idx], text: r.text });
  }
  return groups.map((g) => {
    const contiguous = g.idx.every((v, i) => i === 0 || v === g.idx[i - 1] + 1);
    const label = contiguous && g.days.length >= 3
      ? `${SHORT_DAY[g.days[0]]}–${SHORT_DAY[g.days[g.days.length - 1]]}`
      : g.days.map((d) => SHORT_DAY[d]).join(', ');
    return `${label} ${g.text}`;
  }).join(' · ');
}

// ------------------------------------------------------------------ dates

/** 'Sep 24' (viewer's zone unless `tz`). */
export function fmtShortDate(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' });
}

/** '10:41 AM' (viewer's zone unless `tz`). */
export function fmtClock(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 'YYYY-MM' -> 'Aug 2026'. */
export function fmtMonth(ym: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})/.exec(ym ?? '');
  if (!m) return '—';
  const i = Number(m[2]) - 1;
  return i >= 0 && i < 12 ? `${MONTHS[i]} ${m[1]}` : '—';
}

/** Whole days from `iso` to `now`: 'today', '1 day ago', '12 days ago'. */
export function daysAgo(iso: string | null | undefined, now: Date): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const d = Math.floor((now.getTime() - t) / 86_400_000);
  if (d <= 0) return 'today';
  return d === 1 ? '1 day ago' : `${d} days ago`;
}

export function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${s}`;
}

// ------------------------------------------------------------------ header: open now

export interface OpenLine { text: string; source: 'live' | 'snapshot' | 'none'; tone: 'ok' | 'bad' | 'muted' }

/** Live when the live check returned Places data, else computed from snapshot hours and labelled with their date. */
export function openLine(data: DrawerData, live: LiveCheck | null, now: Date): OpenLine {
  const tz = data.header.tz;
  const lp = live?.places && live.places.ok ? live.places.data : null;
  if (lp && lp.openNow !== null) {
    const at = fmtClock(live!.checkedAt);
    if (lp.openNow) {
      const close = lp.nextCloseTime ? closeLabel(lp.nextCloseTime, now, tz) : null;
      return { text: `Open now${close ? `, closes ${close}` : ''} (live ${at})`, source: 'live', tone: 'ok' };
    }
    return { text: `Closed now (live ${at})`, source: 'live', tone: 'bad' };
  }
  const st = openFromHours(data.header.hours, now, tz);
  const dated = data.header.hoursMeasuredAt ? `hours as of ${fmtShortDate(data.header.hoursMeasuredAt)}` : 'hours date unknown';
  if (!st) {
    return { text: data.header.hours?.length ? `Open now: can't tell from the saved hours (${dated})` : 'Hours not measured', source: 'none', tone: 'muted' };
  }
  if (st.open24) return { text: `Open 24 hours (${dated})`, source: 'snapshot', tone: 'ok' };
  if (st.open) return { text: `Open now${st.closesAtMin !== null ? `, closes ${fmtMinutes(st.closesAtMin)}` : ''} (${dated})`, source: 'snapshot', tone: 'ok' };
  return { text: `Closed now (${dated})`, source: 'snapshot', tone: 'bad' };
}

function closeLabel(iso: string, now: Date, tz: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    const sameDay = zonedParts(d, tz).day === zonedParts(now, tz).day && d.getTime() - now.getTime() < 86_400_000;
    return d.toLocaleString('en-US', sameDay
      ? { timeZone: tz, hour: 'numeric', minute: '2-digit' }
      : { timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit' });
  } catch { return null; }
}

/** Week hours to show: live (Google Maps) when present, else the snapshot. */
export function hoursLineFor(data: DrawerData, live: LiveCheck | null): { text: string; live: boolean; measuredAt: string | null } | null {
  const lp = live?.places && live.places.ok ? live.places.data : null;
  const liveLine = weekLine(weekFromDescriptions(lp?.weekdayDescriptions));
  if (liveLine) return { text: liveLine, live: true, measuredAt: live!.checkedAt };
  const snap = data.map?.hoursLine ?? weekLine(data.header.hours);
  return snap ? { text: snap, live: false, measuredAt: data.header.hoursMeasuredAt ?? data.map?.measuredAt ?? null } : null;
}

// ------------------------------------------------------------------ the hook

export interface HookView {
  /** The hook the setter would use now: the primary, or the backup once a live check withdrew the primary. */
  hook: DrawerHook | null;
  status: HookLiveStatus;
  statusText: string;
  /** The primary, when the live check says it changed (shown struck through with the reason). */
  withdrawn: DrawerHook | null;
  withdrawnReason: string | null;
  backup: DrawerHook | null;
  isBackupPromoted: boolean;
}

export function hookView(data: DrawerData, live: LiveCheck | null): HookView {
  const primary = data.hook.primary;
  const backup = data.hook.backup;
  const closed = !!live?.banner;
  const changed = !!primary && !!live && (live.hookStatus === 'changed' || closed);
  if (!primary) {
    return { hook: null, status: 'unchecked', statusText: '', withdrawn: null, withdrawnReason: null, backup: null, isBackupPromoted: false };
  }
  if (changed) {
    const reason = live!.hookStatusReason ?? live!.banner ?? 'A live check no longer matches it.';
    return {
      hook: closed ? null : backup, status: 'unchecked',
      statusText: backup && !closed ? 'Backup hook: open the evidence before you Verify.' : '',
      withdrawn: primary, withdrawnReason: reason, backup: null, isBackupPromoted: !!backup && !closed,
    };
  }
  const status: HookLiveStatus = live ? live.hookStatus : 'unchecked';
  const at = live ? fmtClock(live.checkedAt) : null;
  let statusText: string;
  if (status === 'still_true') statusText = `Still true (live ${at})`;
  else if (live && live.hookStatusReason) statusText = `Unchecked: ${live.hookStatusReason}. Open the evidence before you Verify.`;
  else statusText = 'Unchecked: open the evidence before you Verify.';
  return { hook: primary, status, statusText, withdrawn: null, withdrawnReason: null, backup, isBackupPromoted: false };
}

/**
 * DrawerData.hook as the page holds it after a Verify: `verifiedHookId` is page memory only (not part of the API), so
 * the "Verified" chip sits on the hook the setter actually verified, even when that was the promoted backup.
 */
export type DrawerHookBlock = DrawerData['hook'] & { verifiedHookId?: number | null };

/** Is the hook on screen the business's verified observation? */
export function hookVerified(data: DrawerData, hv: HookView): boolean {
  const h = hv.hook;
  if (!h || !data.hook.observationVerifiedAt) return false;
  const vid = (data.hook as DrawerHookBlock).verifiedHookId;
  if (vid !== undefined && vid !== null) return vid === h.id;
  return !hv.isBackupPromoted && h.id === data.hook.primary?.id;
}

export const USAGE_LABEL: Record<HookUsage, string> = { opener: 'opener OK', mid_call: 'mid-call only', meeting: 'meeting only' };

export interface VerifyGate { show: boolean; enabled: boolean; why: string | null }

/**
 * Verify writes the hook's sentence as the business observation the Script panel opens with, so:
 * - only opener hooks that passed their detector check (openerEligible) and pass OPENER_NEVER can be verified;
 * - a live "still true" makes it one click; otherwise the setter must open the evidence link first (SETTER-PLAYBOOK §3);
 * - read-only views (the admin Results tab) never verify.
 */
export function verifyGate(args: {
  hook: DrawerHook | null; status: HookLiveStatus; evidenceOpened: boolean; verified: boolean; readOnly: boolean; busy: boolean;
}): VerifyGate {
  const h = args.hook;
  if (!h || args.readOnly || args.verified) return { show: false, enabled: false, why: null };
  if (h.usage !== 'opener' || !h.openerEligible) {
    return { show: false, enabled: false, why: h.usage === 'opener' ? "This hook's detector hasn't passed its accuracy check yet, so it can't open the call." : 'Use it later in the call, never as the opener.' };
  }
  if (OPENER_NEVER.test(h.sayText)) return { show: false, enabled: false, why: 'This sentence has a word we never say in an opener. Don\'t use it; tell PJ.' };
  if (!/^https?:\/\//i.test(h.evidenceUrl)) return { show: false, enabled: false, why: 'No web link for the evidence, so it can\'t be verified.' };
  if (args.busy) return { show: true, enabled: false, why: 'Saving…' };
  if (args.status === 'still_true' || args.evidenceOpened) return { show: true, enabled: true, why: null };
  return { show: true, enabled: false, why: 'Open the evidence first and check it says this.' };
}

/** Same observation sentence, ignoring a leading "…" (LG-SIGNALS templates start with one) and spacing. */
export function sameSentence(a: string | null | undefined, b: string | null | undefined): boolean {
  const norm = (x: string) => x.replace(/^\s*(…|\.\.\.)\s*/, '').replace(/\s+/g, ' ').trim().toLowerCase();
  return typeof a === 'string' && typeof b === 'string' && norm(a) !== '' && norm(a) === norm(b);
}

/** The hook id sent with /api/calls/prepare (CallPrepareRequest.hookId): the hook on screen, if any. */
export function shownHookId(data: DrawerData | null, live: LiveCheck | null): number | null {
  if (!data) return null;
  return hookView(data, live).hook?.id ?? null;
}

/** A one-lead research (§4.4) is still in progress: the drawer hides its Research button and re-reads itself. */
export function researchRunning(data: DrawerData | null): boolean {
  const s = data?.research.status;
  return s === 'draft' || s === 'estimated' || s === 'running';
}

/**
 * The Script panel's hint when there is no verified observation, so observation openers are hidden. Where a setter
 * verifies one depends on the lead (LEADGEN-SPEC §11, §13 AI-TRAINING 5):
 * - researched by the lead engine (`researchAt`): the Business drawer's hook, the only place with its evidence and live
 *   status (the Brief's Research button is hidden). When the drawer has no hook that may open the call, it says so.
 * - not researched (a CSV lead): when the lead card can verify its imported observation (`cardCanVerify`, 2026-10-10:
 *   observationLine's canUse), the card's Check + Use in opener, which is free; otherwise the drawer's "Research this
 *   business" when it offers one (paid, approval-gated).
 * `data` / `live` are the drawer's snapshot and live check for this lead (null until the prefetch lands).
 */
export function observationHint(args: { researchAt: string | null; data: DrawerData | null; live: LiveCheck | null; cardCanVerify?: boolean }): string {
  const lead = 'No verified observation for this business, so observation openers are hidden.';
  const { data } = args;
  if (args.researchAt || data?.researched) {
    if (!data) return `${lead} Open the Business drawer (I) and Verify the hook to unlock them.`;
    const hv = hookView(data, args.live);
    const gate = verifyGate({ hook: hv.hook, status: hv.status, evidenceOpened: true, verified: false, readOnly: false, busy: false });
    return gate.show
      ? `${lead} Open the Business drawer (I) and Verify the hook to unlock them.`
      : `${lead} The Business drawer (I) has no hook that can open this call.`;
  }
  // The pre-call brief (and its Verify) left the dial screen on 2026-10-01 (REVIEW-2026-10-01): the drawer is the only place.
  if (args.cardCanVerify) return `${lead} Click Check on the "Seen online" line of the lead card; if it still holds, press Use in opener.`;
  if (researchRunning(data)) return `${lead} Wait for the research in the Business drawer (I) to finish, then Verify its hook.`;
  if (data?.research.available) return `${lead} Research this business from the Business drawer (I), then Verify its hook, to unlock them.`;
  return `${lead} Use an opener without one.`;
}

/** "Don't say" words: the server's list (OPENER_NEVER words plus lead-flag lines), or the spec's words when it sent none. */
export function dontSayLine(data: DrawerData): string {
  const words = data.hook.dontSay.length ? data.hook.dontSay : [...OPENER_NEVER_WORDS];
  return words.join(', ');
}

// ------------------------------------------------------------------ live-check policy (§11 Data)

/** Parts of a live check that re-test what the primary hook depends on and that cost nothing (the site fetch). */
export function hookNeedsSite(data: DrawerData | null): boolean {
  return !!data?.hook.primary && data.hook.primary.liveDependsOn.includes('site');
}

export function placesAllowed(data: DrawerData | null): boolean {
  return !!data && !!data.placeId && data.live.placesAvailable;
}

/** When the lead appears: only the free site check, and only when the primary hook depends on the site. */
export function partsOnAppear(data: DrawerData | null): LivePart[] {
  return hookNeedsSite(data) ? ['site'] : [];
}

/**
 * When the drawer opens: Places once per lead (policy 'on_open' and 'on_open_and_connect' both check on open), plus the
 * site when the hook depends on it, so the newest check always covers everything checked before (the newest replaces).
 */
export function partsOnOpen(data: DrawerData | null, placesTried: boolean): LivePart[] {
  if (!data) return [];
  if (!placesAllowed(data) || placesTried) return [];
  return hookNeedsSite(data) ? ['places', 'site'] : ['places'];
}

/** When the call is answered: Places only with the 'on_open_and_connect' policy, once per lead. */
export function partsOnConnect(data: DrawerData | null, placesTried: boolean): LivePart[] {
  const connectPolicy: LiveCheckPolicy = 'on_open_and_connect';
  if (!data || data.live.policy !== connectPolicy) return [];
  return partsOnOpen(data, placesTried);
}

/** R: re-run everything that applies (Places when allowed, the site when there is one to fetch). */
export function partsOnRecheck(data: DrawerData | null): LivePart[] {
  if (!data) return [];
  const parts: LivePart[] = [];
  if (placesAllowed(data)) parts.push('places');
  if (hookNeedsSite(data) || data.header.website) parts.push('site');
  return parts;
}

// ------------------------------------------------------------------ blocks

export function ratingLine(data: DrawerData, live: LiveCheck | null): { text: string; live: boolean } | null {
  const lp = live?.places && live.places.ok ? live.places.data : null;
  if (lp && lp.rating !== null && lp.userRatingCount !== null) {
    return { text: `${lp.rating.toFixed(1)} stars, ${lp.userRatingCount} review${lp.userRatingCount === 1 ? '' : 's'}`, live: true };
  }
  const h = data.header;
  if (h.rating === null && h.reviewsCount === null) return null;
  const parts: string[] = [];
  if (h.rating !== null) parts.push(`${h.rating.toFixed(1)} stars`);
  if (h.reviewsCount !== null) parts.push(`${h.reviewsCount} review${h.reviewsCount === 1 ? '' : 's'}`);
  return { text: parts.join(', '), live: false };
}

export function categoryLine(data: DrawerData): string | null {
  const c = data.header.categoryName;
  if (!c) return null;
  return data.header.extraCategories > 0 ? `${c} +${data.header.extraCategories}` : c;
}

export function rankLine(r: MapRankEntry): string {
  if (r.notInTop100) return `"${r.term}": not in the top 100 (${r.points} point${r.points === 1 ? '' : 's'}, ${fmtShortDate(r.measuredAt)})`;
  const parts: string[] = [];
  if (r.median !== null) parts.push(`${ordinal(r.median)} median`);
  if (r.best !== null) parts.push(`best ${ordinal(r.best)}`);
  return `"${r.term}": ${parts.length ? parts.join(', ') : 'not measured'} (${r.points} point${r.points === 1 ? '' : 's'}, ${fmtShortDate(r.measuredAt)})`;
}

export function replyRateLine(rate: number | null, reviewsCount: number | null): string {
  if (rate === null) return 'owner replies: not measured';
  const base = reviewsCount !== null ? Math.min(20, reviewsCount) : null;
  return `owner replied to ${Math.round(rate * 100)}% of the latest ${base ?? 'reviews (up to 20)'}`;
}

export function paceLine(n12m: number | '50+' | null): string {
  if (n12m === null) return '12-month pace: not measured';
  return `${n12m} in the last 12 months`;
}

const MENTION_KIND: Record<string, string> = {
  no_answer: 'no answer', voicemail: 'voicemail', no_callback: 'no callback', hard_to_reach: 'hard to reach', other: 'other',
};
export function mentionMeta(m: ReviewMention): string {
  const stars = m.rating !== null ? `${m.rating} star${m.rating === 1 ? '' : 's'}` : 'stars not measured';
  const kind = m.detail ? ` · ${MENTION_KIND[m.detail] ?? m.detail.replace(/_/g, ' ')}` : '';
  return `${stars} · ${fmtMonth(m.month)}${kind}`;
}

/** Up to 3 complaints then 1 praise (the server already caps; this keeps the rule even if it doesn't). */
export function shownMentions(data: DrawerData): ReviewMention[] {
  const c = data.phoneMentions.complaints.slice(0, 3);
  return data.phoneMentions.praise ? [...c, data.phoneMentions.praise] : c;
}

/** Were reviews read at all? Without it, "no complaints" would be a claim we can't make. */
export function reviewsMeasured(data: DrawerData): boolean {
  return !!data.map && (data.map.newestReviewAt !== null || data.map.n12m !== null);
}

export function lcpLine(ms: number | null): string | null {
  if (ms === null) return null;
  return `main content about ${Math.floor(ms / 1000)} s (lab, mobile)`;
}

export function yesNo(v: boolean | null | undefined): string {
  return v === null || v === undefined ? 'not measured' : v ? 'yes' : 'no';
}

export function hostOf(url: string | null | undefined): string {
  if (!url) return '';
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

export function isWebLink(url: string | null | undefined): url is string {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

/** Site loads: the live fetch when it ran, else the snapshot. */
export function siteLoadsLine(data: DrawerData, live: LiveCheck | null): { text: string; tone: 'ok' | 'bad' | 'muted' } {
  if (live?.site) {
    if (!live.site.ok) return { text: `live check failed: ${live.site.reason}`, tone: 'muted' };
    const ok = live.site.data.status === 'ok';
    return { text: `${ok ? 'yes' : `no (${live.site.data.status.replace(/_/g, ' ')})`} (live ${fmtClock(live.checkedAt)})`, tone: ok ? 'ok' : 'bad' };
  }
  const w = data.website;
  if (!w || w.loads === null) return { text: 'not measured', tone: 'muted' };
  return { text: `${w.loads ? 'yes' : 'no'} (checked ${fmtShortDate(w.checkedAt)})`, tone: w.loads ? 'ok' : 'bad' };
}

export function historyLine(c: CallSummary, dispositionLabel: (code: DispositionCode) => string, tz?: string): string {
  const when = `${fmtShortDate(c.createdAt, tz)} ${fmtClock(c.createdAt, tz)}`;
  const what = c.disposition ? dispositionLabel(c.disposition) : c.direction === 'inbound' ? 'Inbound call' : 'No outcome logged';
  const vm = c.vmType ? ` · VM ${c.vmType}` : '';
  const who = c.userName ? ` · ${c.userName}` : '';
  return `${when} · ${what}${vm}${who}`;
}

export function countLine(label: string, used: number | null, max: number | null): string | null {
  if (used === null) return null;
  return `${label} ${used}${max !== null ? `/${max}` : ''}`;
}

export function scoreLine(s: OfferScore): string {
  return `fit ${s.fit} · reach ${s.reach.toFixed(2)} · hook ${s.hookStrength} · priority ${s.priority} · tier ${s.tier}`;
}

export const OFFER_LABEL: Record<string, string> = { front_desk: 'Get Answered', website: 'Get Found: website', gbp: 'Get Found: local SEO' };

/** "+35 phone reviews · +20 after-hours": the server's reason texts in one line. */
export function reasonsLine(data: DrawerData): string | null {
  const r = data.footer.reasons;
  if (!r.length) return null;
  return r.map((x) => x.text).join(' · ');
}

export function usdLabel(usd: number): string {
  return `$${usd.toFixed(2)}`;
}
